import { Injectable, inject } from '@angular/core';

import { createSelector, Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import type { UserIdMap } from '@core/account-data';
import { filterBoolean } from '@core/common-legacy';
import { DepartmentSelectors } from '@core/department-data';
import { memoizeSelector } from '@core/ngrx-legacy';
import type {
  Requisition,
  RequisitionAssignedFlow,
  RequisitionAvailableFlow,
} from '@core/requisitions-types';
import {
  AssignedFlowsSelectors,
  AvailableFlowsSelectors,
  RequisitionsSelectors,
} from '@recruitee/requisitions-data';
import { RequisitionsConfigSelectors } from '@recruitee/requisitions-settings-data';

import { RootPageDataService } from './root-page-data.service';

const departmentNameIfModeEnabled = memoizeSelector((requisitionId: number) =>
  createSelector(
    RequisitionsConfigSelectors.isDepartmentsMode,
    RequisitionsSelectors.byId(requisitionId),
    DepartmentSelectors.entities(),
    (isDepartmentsMode, requisition, departments) => {
      if (!isDepartmentsMode || !requisition?.departmentId) return undefined;
      return departments.find(dep => dep.id === requisition.departmentId)?.name;
    },
  ),
);

const isLoaded = memoizeSelector((requisitionId: number) =>
  createSelector(
    AssignedFlowsSelectors.isLoaded({ requisitionId }),
    AvailableFlowsSelectors.isLoaded(requisitionId),
    (a, b) => a && b,
  ),
);

@Injectable()
export class FlowPageDataService {
  private readonly rootData = inject(RootPageDataService);
  private readonly store = inject(Store);

  public requisition$: Observable<Requisition>;
  public approvalFlow$: Observable<RequisitionAssignedFlow | null>;
  public adminsMap$: Observable<UserIdMap>;
  public flowsList$: Observable<RequisitionAvailableFlow[]>;
  public isDepartmentsMode$: Observable<boolean>;
  public departmentName$: Observable<string | undefined>;
  public isLoaded$: Observable<boolean>;

  constructor() {
    const rootData = this.rootData;
    const store = this.store;

    this.requisition$ = rootData.requisition$;
    this.approvalFlow$ = this.requisition$.pipe(
      switchMap(requisition =>
        store.select(AssignedFlowsSelectors.byRequisitionId({ requisitionId: requisition.id })),
      ),
    );
    this.adminsMap$ = rootData.adminsMap$;
    this.flowsList$ = this.requisition$.pipe(
      switchMap(requisition =>
        store.select(AvailableFlowsSelectors.byId(requisition.id)).pipe(
          filterBoolean,
          map(entity => entity.approvalFlows),
        ),
      ),
    );
    this.isDepartmentsMode$ = this.store.select(RequisitionsConfigSelectors.isDepartmentsMode);
    this.departmentName$ = this.requisition$.pipe(
      switchMap(requisition => store.select(departmentNameIfModeEnabled(requisition.id))),
    );
    this.isLoaded$ = this.requisition$.pipe(
      switchMap(requisition => store.select(isLoaded(requisition.id))),
    );
  }
}
